// Private credentials never enter fetch URLs, router state, or a global "last quote" slot.
// A history-entry reference binds the current page to tab-scoped storage. Back/refresh
// can restore that exact entry; opening a bare or different quote cannot reuse it.
const PREFIX = "xa.private.access.v1.";
const STATE_KEY = "xaPrivateAccess";
const memory = new Map();
const ACTIONS = ["accept", "decline", "changes"];
const reference = () => Array.from(window.crypto.getRandomValues(new Uint8Array(18)), value => value.toString(16).padStart(2, "0")).join("");
const validRef = value => typeof value === "string" && /^[a-f0-9]{36}$/.test(value);
const stateRef = () => window.history.state?.[STATE_KEY] || window.history.state?.usr?.[STATE_KEY];
const empty = (kind, id = "") => ({ kind, id, token: "", action: "", ref: "" });

function read(ref) {
  if (!validRef(ref)) return null;
  try {
    const stored = window.sessionStorage.getItem(PREFIX + ref);
    if (stored) return JSON.parse(stored);
  } catch { /* Memory supports this page if session storage is blocked. */ }
  return memory.get(ref) || null;
}
function save(record) {
  memory.set(record.ref, record);
  try { window.sessionStorage.setItem(PREFIX + record.ref, JSON.stringify(record)); } catch { /* No persistence outside this page. */ }
}
function parse(url, kind, id) {
  const hash = new URLSearchParams(url.hash.slice(1));
  const hasToken = hash.has("token") || hash.has("response_token") || url.searchParams.has("token") || url.searchParams.has("response_token");
  const token = hash.has("token") ? hash.get("token") : hash.has("response_token") ? hash.get("response_token") : url.searchParams.get("token") ?? url.searchParams.get("response_token") ?? "";
  const quoteId = kind === "quote" ? hash.get("id") ?? url.searchParams.get("id") ?? "" : id;
  const action = hash.get("action") ?? url.searchParams.get("action") ?? "";
  const secrets = [hash, url.searchParams].flatMap(params => ["token", "response_token"].flatMap(key => params.getAll(key))).filter(Boolean);
  for (const key of ["token", "response_token"]) { hash.delete(key); url.searchParams.delete(key); }
  // Do not copy a secret hidden again under an unrelated parameter name.
  for (const params of [hash, url.searchParams]) for (const [key, value] of [...params]) {
    if (secrets.some(secret => key.includes(secret) || value.includes(secret))) params.delete(key);
  }
  if (kind === "quote") for (const key of ["id", "action"]) { hash.delete(key); url.searchParams.delete(key); }
  url.hash = hash.toString() ? `#${hash}` : "";
  return { hasToken, token, id: quoteId, action: ACTIONS.includes(action) ? action : "" };
}

export function consumePrivateAccess(kind, id = "") {
  const url = new URL(window.location.href);
  const parsed = parse(url, kind, id);
  let record = null;
  if (parsed.hasToken) {
    // Even an empty explicit token replaces prior access. Never fall back silently.
    record = { kind, id: parsed.id, token: parsed.token, action: parsed.action, path: url.pathname, ref: reference() };
  } else {
    const returned = kind === "payment" && ["success", "cancel"].includes(url.searchParams.get("checkout"));
    const quoteId = returned ? url.searchParams.get("quote_id") : null;
    let returnRef = null;
    if (quoteId) {
      try { returnRef = window.sessionStorage.getItem(PREFIX + "quote." + quoteId); } catch { /* Memory fallback below. */ }
      returnRef = returnRef || memory.get("quote." + quoteId);
    }
    record = read(quoteId ? returnRef : stateRef());
    if (quoteId && record?.id !== quoteId) record = null;
    if (record && (record.kind !== kind || record.path !== url.pathname || (id && record.id !== id) || (parsed.id && record.id !== parsed.id))) record = null;
    if (record && parsed.action) record = { ...record, action: parsed.action };
  }
  url.searchParams.delete("quote_id");
  const state = { ...window.history.state };
  if (record) state[STATE_KEY] = record.ref;
  else {
    delete state[STATE_KEY];
    if (state.usr && typeof state.usr === "object") { state.usr = { ...state.usr }; delete state.usr[STATE_KEY]; }
  }
  try {
    // This must succeed before callers may send an authenticated request.
    window.history.replaceState(state, "", url.pathname + url.search + url.hash);
  } catch { return empty(kind, id); }
  if (record) save(record);
  // Do not revive the former shared payment-token slot: it could belong to another quote.
  try { window.sessionStorage.removeItem("xa.payment.access"); } catch { /* Storage can be blocked. */ }
  return record || empty(kind, id);
}

// Bind Checkout return restoration only after the server verified this credential.
export function bindPaymentQuote(access, quoteId) {
  if (!access.token || typeof quoteId !== "string" || !quoteId) return;
  const record = { ...access, id: quoteId };
  save(record);
  memory.set("quote." + quoteId, access.ref);
  try { window.sessionStorage.setItem(PREFIX + "quote." + quoteId, access.ref); } catch { /* Memory remains available. */ }
}

export function consumeInitialPrivateAccess() {
  const path = window.location.pathname;
  if (path === "/quote/respond") return consumePrivateAccess("quote");
  if (path === "/payment") return consumePrivateAccess("payment");
  const schedule = path.match(/^\/schedule\/([^/]+)$/);
  if (schedule) {
    let id = schedule[1];
    try { id = decodeURIComponent(id); } catch { /* Invalid IDs still have secrets removed. */ }
    return consumePrivateAccess("schedule", id);
  }
  return null;
}

// Convert a server-issued fragment link into a clean SPA navigation. The secret
// stays in session storage, and only the opaque non-credential reference is routed.
export function privateNavigation(raw, expectedKind) {
  const url = new URL(raw, window.location.origin);
  if (url.origin !== window.location.origin) throw new Error("Invalid private destination");
  const match = url.pathname.match(/^\/schedule\/([^/]+)$/);
  if ((expectedKind === "payment" && url.pathname !== "/payment") || (expectedKind === "schedule" && !match)) throw new Error("Invalid private destination");
  const parsed = parse(url, expectedKind, match ? decodeURIComponent(match[1]) : "");
  if (!parsed.token) throw new Error("Missing private access");
  const record = { kind: expectedKind, id: parsed.id, token: parsed.token, action: "", path: url.pathname, ref: reference() };
  save(record);
  return { to: url.pathname + url.search, state: { [STATE_KEY]: record.ref } };
}
